import {Apollo, gql} from 'apollo-angular';
import {DocumentNode, FetchResult} from '@apollo/client/core';

import {Injectable} from '@angular/core';

import {PointVente} from '@models/pointVente.model';
import * as moment from 'moment';
import {BaseRepository} from '@galec/repositories/baseRepository';
import {combineLatest, Observable} from 'rxjs';

import {map, switchMap} from 'rxjs/operators';
import {ALL_HORAIRE_FIELD} from "@galec/repositories/horaire.gql";
import {ALL_HORAIRE_EXCEPTIONNELLE_FIELD} from "@galec/repositories/ouverturesFermeturesExceptionnelles.gql";
import {UserAuthenticationService} from "@galec/services/user/user-authentication.service";
import {Activitecommerciale} from "@models/activitecommerciale.model";
import {User} from "@models/user.model";
import {Centrale} from "@galec/database/models/backend";
import {ConceptUtils} from "@enums/concept.enum";
import {ALL_INFORMATIONS_REGLEMENTAIRES_PARITE_FIELD} from "@galec/repositories/infosReglementairesParite.gql";


@Injectable()
/**
 * Horaire repository service.
 */
export class PointVenteRepository extends BaseRepository {

    protected DELETE_QUERY: DocumentNode;
    protected INSERT_QUERY: DocumentNode;
    protected LIST_QUERY: DocumentNode = SelectPointVenteEtHorairesGql;
    protected UPDATE_QUERY: DocumentNode = UpdatePointVenteGql;

    constructor(protected apollo: Apollo, protected userAuthenticationService: UserAuthenticationService) {
        super();
        this.init();
    }

    /**
     * @inheritDoc
     */
    public select(variables?: any): Observable<Map<string, PointVente>> {
        // And current date to query.
        variables.date = moment().format('YYYY-MM-DD');
        return super.select(variables).pipe(map(this.formatPointVente));
    }

    public loadPointVenteWithConcepts() {
        return this.queryHandler(LoadAllPointVente, {
            codePanonceau: this.connectedUser.codePanonceau,
            date: moment().format('YYYY-MM-DD')
        })
            .pipe(map(this.formatPointVente));
    }

    public loadPointVenteWithConceptsWithId(id_pointvente: string) {
        return this.queryHandler(LoadAllPointVenteWithId, {
            id_pointvente,
            date: moment().format('YYYY-MM-DD')
        })
            .pipe(map(this.formatPointVente));
    }

    public loadPointVente(): Observable<any> {
        return combineLatest([
            this.getHyper(),
            this.getConcepts()
        ]);
    }

    public getHyper() {
        return this.watchAndSubscribe(GetHyper_Query, GetHyper_Subscription, {
            codePanonceau: this.connectedUser.codePanonceau,
            date: moment().format('YYYY-MM-DD')
        })
            .pipe(map(this.formatPointVente))
            .pipe(map((res: any) => {
                return res.size > 0 ? [...res.values()][0] : null;
            }));
    }

    public getMasters() {
        return this.watchAndSubscribe(GetHyper_Query, GetHyper_Subscription, {
            codePanonceau: this.connectedUser.codePanonceau,
            date: moment().format('YYYY-MM-DD')
        });
    }

    public getHyperSuperExpressProxi(codePanonceau: string) {
        return this.queryHandler(getHyperSuperExpressProxi_Query, {codePanonceau, date : moment().format('YYYY-MM-DD')});
    }

    public getConcepts() {
        return this.watchAndSubscribe(GetConcepts_Query, GetConcepts_Subscription, {
            codePanonceau: this.connectedUser.codePanonceau,
            date: moment().format('YYYY-MM-DD')
        })
            .pipe(map((result: any) => {
                const activiteCommerciales: Map<string, Activitecommerciale> = new Map<string, Activitecommerciale>();
                result.data?.activitecommerciale?.forEach((value, key) => {
                    const activiteCommerciale = new Activitecommerciale(value);
                    const codeActivite = activiteCommerciale.pointventes?.values()?.next()?.value?.code_activite ?? null;
                    const isConcept = codeActivite != null && ConceptUtils.getValuesAsList().includes(codeActivite);
                    if (activiteCommerciale.pointventes.size > 0 && !isConcept) {
                        activiteCommerciales.set(activiteCommerciale.id, activiteCommerciale);
                    }
                });
                return this.sortActiviteByOrdre(activiteCommerciales);
            }));
    }

    public getConceptsWithMasterId(id_pointvente_master: string) {
        return this.watchAndSubscribe(GetConceptsWithMasterId_Query, GetConceptsWithMasterId_Subscription, {
            id_pointvente_master,
            date: moment().format('YYYY-MM-DD')
        })
            .pipe(map((result: any) => {
                const activiteCommerciales: Map<string, Activitecommerciale> = new Map<string, Activitecommerciale>();
                result.data?.activitecommerciale?.forEach((value, key) => {
                    const activiteCommerciale = new Activitecommerciale(value);
                    const codeActivite = activiteCommerciale.pointventes?.values()?.next()?.value?.code_activite ?? null;
                    const isConcept = codeActivite != null && ConceptUtils.getValuesAsList().includes(codeActivite);
                    if (activiteCommerciale.pointventes.size > 0 && !isConcept) {
                        activiteCommerciales.set(activiteCommerciale.id, activiteCommerciale);
                    }
                });
                return this.sortActiviteByOrdre(activiteCommerciales);
            }));
    }

    /**
     * Trie les activitÃ©s commerciales fournies par ordre croissant basÃ© sur la propriÃ©tÃ© "activite_ordre".
     * Les Ã©lÃ©ments avec un ordre Ã©gal Ã  0 sont placÃ©s Ã  la fin.
     *
     * @param activiteCommerciales - Une Map contenant les activitÃ©s commerciales Ã  trier.
     * @return Une nouvelle Map contenant les activitÃ©s commerciales triÃ©es par ordre croissant.
     */
    private sortActiviteByOrdre(activiteCommerciales: Map<string, Activitecommerciale>): Map<string, Activitecommerciale> {
        return new Map([...activiteCommerciales.entries()]
            .sort((a, b) => {
                const ordreA = a[1].pointventes?.values()?.next()?.value?.activite_ordre ?? 0;
                const ordreB = b[1].pointventes?.values()?.next()?.value?.activite_ordre ?? 0;

                // Les Ã©lÃ©ments avec ordre 0 vont Ã  la fin
                if (ordreA === 0 && ordreB !== 0) return 1;
                if (ordreB === 0 && ordreA !== 0) return -1;

                // Tri normal par ordre croissant
                return ordreA - ordreB;
            })
        );
    }

    /**
     * Select point vente by key.
     * @param key string    Key to filter with.
     * @param user User  User Object.
     * @return Observable<any>      Observable with returned value.
     */
    public selectByKey(key: string, user?: User, isWithPdvInactive?: boolean): Observable<any> {
        const variables: any = {key: `%${key}%`};
        // Set weired name to central if not admin.
        variables.keyCentral = user.connectedAs === 'national' ? variables.key : 'NULL';
        // Set panonceau if is central.
        if (user.codePanonceauCentral && user.connectedAs === 'central') {
            variables.codePanonceauCentrale = user.codePanonceauCentral;
        }
        return this.queryHandler(isWithPdvInactive ? (user.codePanonceauCentral ?
                SelectPointVenteAutocompleteWithInactive : SelectPointVenteAutocompleteWithInactiveNational) :
            (user.codePanonceauCentral ? SelectPointVenteAutocomplete : SelectPointVenteAutocompleteNational), variables)
            .pipe(map((result: any) => {
                const list = [];
                result.data?.pointvente?.forEach((value) => {
                    list.push({
                        code_panonceau: value.code_panonceau,
                        id: value.id_base_adherent,
                        id_base_adherent_acdelec: value.id_base_adherent_acdelec,
                        cp: value.cp,
                        libelle: value.libelle,
                        ville: value.ville,
                        type: 'pointVente',
                        adresse: value.adresse,
                        est_actif: value.est_actif,
                        centrale: value.zonechalandise?.centrale?.nom ?? ''
                    });
                });
                result.data?.centrale?.forEach((value: Centrale) => {
                    list.push({
                        code_panonceau: '',
                        id: value.code_panonceau_centrale,
                        cp: '',
                        libelle: value.nom,
                        ville: '',
                        type: 'central',
                    });
                });
                return list;
            }));
    }

    public selectPointVenteByCodePanonceauIsActive(codePanonceau: string): Observable<any> {
        return this.queryHandler(SelectAllpointventeWithCodePanonceauIsActive, {codePanonceau});
    }

    public selectPointVenteByCodePanonceau(codePanonceau: string): Observable<any> {
        return this.queryHandler(SelectAllpointventeWithCodePanonceau, {codePanonceau});
    }

    public selectActiviteCommerciale(key?: any): Observable<any> {
        return this.queryHandler(selectActiviteCommerciale, key);
    }

    public selectPointDeVenteByiD(id_base_adherent: any): Observable<any> {
        return this.apollo.query({
            query: selectPointDeVenteByiD,
            variables: {id_base_adherent},
        });
    }

    public selectPointDeVenteByIdsBadh(idsBadh: string[]): Observable<any> {
        return this.apollo.query({
            query: selectPointDeVenteByIdsBadh,
            variables: {idsBadh: idsBadh},
        });
    }

    public updatePointDeVente(old_id_base_adherent: string, new_id_base_adherent: string, code_panonceau: string, code_activite: string, numero_ordre: number, est_master: boolean, est_concept: boolean): Observable<FetchResult<{}>> {
        return this.mutationHandler(UpdatePointVenteIdentifiant, {
            old_id_base_adherent: old_id_base_adherent,
            new_id_base_adherent: new_id_base_adherent,
            code_panonceau: code_panonceau,
            code_activite: code_activite,
            numero_ordre: numero_ordre,
            est_master: est_master,
            est_concept: est_concept,
        });
    }

    public deletePointVenteMasterByConceptId(id_pointvente_concept: string): Observable<FetchResult<{}>> {
        return this.mutationHandler(DeletePointVenteMasterByConceptId, {id_pointvente_concept});
    }

    public selectNumSiren(num_siren: any): Observable<any> {
        return this.queryHandler(numSirenInfo, {num_siren});
    }

    public selectNifSpain(nif_espagnol: any): Observable<any> {
        return this.queryHandler(nifSpainInfo, {nif_espagnol});
    }

    public selectNifPortugal(nif_portugal: any): Observable<any> {
        return this.queryHandler(nifPortugalInfo, {nif_portugal});
    }

    public selectMs(ms: any): Observable<any> {
        return this.queryHandler(msInfo, {ms});
    }

    public selectNumOrdre(codePanonceau_codeActivite: any): Observable<any> {
        const id_base_adherent: any = {codePanonceau_codeActivite: `%${codePanonceau_codeActivite}%`};
        return this.queryHandler(selectNumeroOrdre, {id_base_adherent: id_base_adherent.codePanonceau_codeActivite});
    }

    /**
     * Update Visuel Point vente.
     * @param value any   Base object.
     * @return Observable<FetchResult<{}>>  Observable with result.
     */
    public updateVisuelPointVente(value: any): Observable<FetchResult<{}>> {
        return this.mutationHandler(UpdatePointVenteVisuel, value);
    }

    public updateDonneAdministratives(numero_siren: any): Observable<FetchResult<{}>> {
        return this.mutationHandler(updateDonnesAdministratives, numero_siren);
    }

    public updateActiviteOrdre(variables): Observable<FetchResult<{}>> {
        return this.mutationHandler(UpdatePointVenteActiviteOrdre, variables);
    }

    public savePointDeVente(pointvente: any): Observable<FetchResult<{}>> {
        const pointventeMasters: any[] = [];

        for (const id of pointvente.master_id_pointventes) {
            pointventeMasters.push({
                id_pointvente_master: id
            });
        }

        return super.mutationHandler(
            pointvente.est_master ? insertPointDeVenteMaster : insertPointDeVenteConcept,
            pointvente.est_master ? {
                adresse: pointvente.adresse,
                code_activite: pointvente.code_activite,
                code_panonceau: pointvente.code_panonceau,
                id_base_adherent_acdelec: pointvente.id_base_adherent_acdelec,
                cp: pointvente.cp,
                telephone: pointvente.telephone,
                url_magasin: pointvente.url_magasin,
                ville: pointvente.ville,
                province: pointvente.province,
                district: pointvente.district,
                est_actif: pointvente.est_actif,
                gps_long: pointvente.gps_long,
                gps_lat: pointvente.gps_lat,
                est_master: pointvente.est_master,
                est_concept: pointvente.est_concept,
                // numero_siren: pointvente.donnee_administrative.numero_siren,
                nom: pointvente.nom,
                libelle: pointvente.libelle,
                id_gouv: pointvente.id_gouv,
                id_base_adherent: pointvente.id_base_adherent,
                numero_ordre: pointvente.numero_ordre,
                donnee_administrative: pointvente.donnee_administrative,
                infos_reglementaires_parite: pointvente.infos_reglementaires_parite,
                horaires: pointvente.horaires
            } : {
                adresse: pointvente.adresse,
                code_activite: pointvente.code_activite,
                code_panonceau: pointvente.code_panonceau,
                id_base_adherent_acdelec: pointvente.id_base_adherent_acdelec,
                cp: pointvente.cp,
                telephone: pointvente.telephone,
                url_magasin: pointvente.url_magasin,
                ville: pointvente.ville,
                province: pointvente.province,
                district: pointvente.district,
                est_actif: pointvente.est_actif,
                gps_long: pointvente.gps_long,
                gps_lat: pointvente.gps_lat,
                est_master: pointvente.est_master,
                est_concept: pointvente.est_concept,
                // numero_siren: pointvente.donnee_administrative.numero_siren,
                nom: pointvente.nom,
                libelle: pointvente.libelle,
                id_gouv: pointvente.id_gouv,
                id_base_adherent: pointvente.id_base_adherent,
                numero_ordre: pointvente.numero_ordre,
                donnee_administrative: pointvente.donnee_administrative,
                infos_reglementaires_parite: pointvente.infos_reglementaires_parite,
                pointvente_masters: pointventeMasters,
                horaires: pointvente.horaires
            }
        );
    }

    // updatePointDeventeDonneAdministrativeHoraires
    public updatePointDeventeDonneAdministrativeHoraires(pointvente: any): Observable<FetchResult<{}>> {
        const pointventeMasters: any[] = [];

        for (const id of pointvente.master_id_pointventes) {
            pointventeMasters.push({
                id_pointvente_master: id
            });
        }

        return super.mutationHandler(
            pointvente.est_master ? updatePointDeventeMasterDonneAdministrativeHoraires : updatePointDeventeConceptDonneAdministrativeHoraires,
            pointvente.est_master ? {
                id_pointvente: pointvente.id_pointvente,
                adresse: pointvente.adresse,
                code_activite: pointvente.code_activite,
                code_panonceau: pointvente.code_panonceau,
                id_base_adherent_acdelec: pointvente.id_base_adherent_acdelec,
                telephone: pointvente.telephone,
                url_magasin: pointvente.url_magasin,
                ville: pointvente.ville,
                cp: pointvente.cp,
                province: pointvente.province,
                district: pointvente.district,
                est_actif: pointvente.est_actif,
                gps_long: pointvente.gps_long,
                gps_lat: pointvente.gps_lat,
                est_master: pointvente.est_master,
                est_concept: pointvente.est_concept,
                // numero_siren: pointvente.donnee_administrative.numero_siren,
                nom: pointvente.nom,
                libelle: pointvente.libelle,
                id_gouv: pointvente.id_gouv,
                id_base_adherent: pointvente.id_base_adherent,
                numero_ordre: pointvente.numero_ordre,
                donnee_administrative: pointvente.donnee_administrative,
                horaires: pointvente.horaires
            } : {
                id_pointvente: pointvente.id_pointvente,
                adresse: pointvente.adresse,
                code_activite: pointvente.code_activite,
                code_panonceau: pointvente.code_panonceau,
                id_base_adherent_acdelec: pointvente.id_base_adherent_acdelec,
                telephone: pointvente.telephone,
                url_magasin: pointvente.url_magasin,
                ville: pointvente.ville,
                cp: pointvente.cp,
                province: pointvente.province,
                district: pointvente.district,
                est_actif: pointvente.est_actif,
                gps_long: pointvente.gps_long,
                gps_lat: pointvente.gps_lat,
                est_master: pointvente.est_master,
                est_concept: pointvente.est_concept,
                // numero_siren: pointvente.donnee_administrative.numero_siren,
                nom: pointvente.nom,
                libelle: pointvente.libelle,
                id_gouv: pointvente.id_gouv,
                id_base_adherent: pointvente.id_base_adherent,
                numero_ordre: pointvente.numero_ordre,
                donnee_administrative: pointvente.donnee_administrative,
                pointvente_masters: pointventeMasters,
                horaires: pointvente.horaires
            }
        );
    }

    public getAllPointVenteNationalIsActif(): Observable<any> {
        return this.queryHandler(GET_ALL_POINT_VENTE_NATIONAL_IS_ACTIF, {isActive: true});
    }

    public getAllPointVenteCentraleIsActif(codePanonceauCentrale: string): Observable<any> {
        return this.queryHandler(GET_ALL_POINT_VENTE_CENTRALE_IS_ACTIF, {codePanonceauCentrale, isActive: true});
    }

    private formatPointVente(result: any): Map<string, PointVente> {
        const pointVentes: Map<string, PointVente> = new Map<string, PointVente>();
        result.data?.pointvente?.forEach((value: PointVente, key) => {
            const pointVente = new PointVente(value);
            pointVentes.set(pointVente.id, pointVente);
        });
        return pointVentes;
    }


}

export const ALL_POINTVENTE_FIELD = `
    id_pointvente
    id_base_adherent
    adresse
    code_activite
    code_panonceau
    id_base_adherent_acdelec
    cp
    est_actif
    est_master
    est_concept
    gps_lat
    gps_long
    nom
    id_gouv
    libelle
    numero_ordre
    numero_siren
    activite_ordre
    telephone
    url_magasin
    ville
    visuel_desktop
    visuel_mobile

    zonechalandise {
      url_facebook
      url_instagram
      url_linkedin
      url_x
      url_tiktok
      url_pinterest
      url_youtube
      url_whatsapp
      url_galerie
      centrale {
        nom
      }
    }
    activitecommerciale {
      libelle
      code_activite
    }
`;

export const ALL_POINTVENTE_MASTER_CONCEPT_FIELD = `
    id_pointvente_concept
    id_pointvente_master
    created_at
    updated_at
`;

/**
 * Point vente horaire query repository.
 */
const SelectPointVenteEtHorairesGql = gql`
    query pointVenteetHoraires($codePanonceau: String!, $date: date) {
        pointvente(where: {id_base_adherent: {_eq: $codePanonceau}, est_actif: {_eq: true}}) {
            ${ALL_POINTVENTE_FIELD}
            horaires {
                ${ALL_HORAIRE_FIELD}
            }
            ouvertures_fermetures_exceptionnelles(where: {_or: [{date_fin: {_gte: $date}}, {date: {_gte: $date}}]},
                order_by: {date: asc}) {
                ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
            }
        }
    }
`;

const SelectAllpointventeWithCodePanonceauIsActive = gql`
    query pointventeWithCodePanonceauIsActive($codePanonceau: String!) {
        pointvente(where: {code_panonceau: {_eq: $codePanonceau}, est_actif: {_eq: true}}) {
            ${ALL_POINTVENTE_FIELD}
        }
    }
`;

const SelectAllpointventeWithCodePanonceau = gql`
    query pointventeWithCodePanonceauIsActive($codePanonceau: String!) {
        pointvente(where: {code_panonceau: {_eq: $codePanonceau}}) {
            ${ALL_POINTVENTE_FIELD}
        }
    }
`;

const GET_HYPER = `
pointvente(
    where: {
        code_panonceau: {_eq: $codePanonceau},
        est_master: {_eq: true},
        est_actif: {_eq: true}
    }
) {
    ${ALL_POINTVENTE_FIELD}

    pointventes_master {
        ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
        master_pointvente {
            ${ALL_POINTVENTE_FIELD}
        }
    }

    pointventes_concept {
        ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
        concept_pointvente {
            ${ALL_POINTVENTE_FIELD}
        }
    }

    pointvente_actualites {
      id_actualite
      id_pointvente
      id_base_adherent
      ordre

      actualite {
        actu_centrale
        date_debut
        date_fin
        id_actualite
        image
        page_detail
        texte
        titre
        draft
        model
        lien
        type
        image_alt

        pointvente_actualites {
          id_actualite
          id_pointvente
          id_base_adherent
          ordre
        }
      }
    }

    horaires {
        ${ALL_HORAIRE_FIELD}
    }
    ouvertures_fermetures_exceptionnelles(
        where: {
            _or: [
                {date_fin: {_gte: $date}},
                {date: {_gte: $date}}
            ]
        },
        order_by: {date: asc}
    ) {
        ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
    }
    informations_reglementaires_parites {
        ${ALL_INFORMATIONS_REGLEMENTAIRES_PARITE_FIELD}
    }

    pointvente_services {
        id_pointvente
        id_base_adherent
        code_service
    }
}`;

const GET_CONCEPTS = `
activitecommerciale(
    where: {
        pointventes: {
            code_panonceau: {_eq: $codePanonceau}
        }
    },
    order_by: {libelle: asc}
) {
    code_activite
    libelle
    pointventes(
        where: {
            est_master: {_eq: false},
            code_panonceau: {_eq: $codePanonceau},
            est_actif: {_eq: true}
        },
        order_by: {numero_ordre: asc}
    )  {
        ${ALL_POINTVENTE_FIELD}
        horaires {
            ${ALL_HORAIRE_FIELD}
        }
        ouvertures_fermetures_exceptionnelles(
            where: {
                _or: [
                    {date_fin: {_gte: $date}},
                    {date: {_gte: $date}}
                ]
            },
            order_by: {date: asc}
        ) {
            ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
        }
    }
}`;

const GET_CONCEPTS_WITH_MASTER_ID = `
activitecommerciale(
    where: {
        pointventes: {
            pointventes_master: {id_pointvente_master: {_eq: $id_pointvente_master}}
        }
    },
    order_by: {libelle: asc}
) {
    code_activite
    libelle
    pointventes(
        where: {
            est_master: {_eq: false},
            pointventes_master: {id_pointvente_master: {_eq: $id_pointvente_master}},
            est_actif: {_eq: true}
        },
        order_by: {numero_ordre: asc}
    )  {
        ${ALL_POINTVENTE_FIELD}
        horaires {
            ${ALL_HORAIRE_FIELD}
        }
        ouvertures_fermetures_exceptionnelles(
            where: {
                _or: [
                    {date_fin: {_gte: $date}},
                    {date: {_gte: $date}}
                ]
            },
            order_by: {date: asc}
        ) {
            ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
        }
    }
}`;

/**
 * Point vente horaire query repository.
 */
const GetHyper_Subscription = gql`
    subscription getHyper($codePanonceau: String!, $date: date) {
        ${GET_HYPER}
    }
`;
const GetHyper_Query = gql`
    query getHyper($codePanonceau: String!, $date: date) {
        ${GET_HYPER}
    }
`;

const getHyperSuperExpressProxi_Query = gql`
    query getHyperSuperExpressProxi($codePanonceau: String!, $date: date) {
        ${GET_HYPER}
    }
`;

const GetConcepts_Subscription = gql`
    subscription getConcepts($codePanonceau: String!, $date: date) {
        ${GET_CONCEPTS}
    }
`;

const GetConceptsWithMasterId_Subscription = gql`
    subscription getConcepts($id_pointvente_master: uuid!, $date: date) {
        ${GET_CONCEPTS_WITH_MASTER_ID}
    }
`;

const GetConcepts_Query = gql`
    query getConcepts($codePanonceau: String!, $date: date) {
        ${GET_CONCEPTS}
    }
`;

const GetConceptsWithMasterId_Query = gql`
    query getConcepts($id_pointvente_master: uuid!, $date: date) {
        ${GET_CONCEPTS_WITH_MASTER_ID}
    }
`;

const SelectPointVenteAutocomplete = gql`
    query SelectPointVenteAutocomplete($codePanonceauCentrale: String, $key: String!, $keyCentral: String!) {
        pointvente(where: {
            est_actif: {_eq: true},
            zonechalandise: {
                code_panonceau_centrale: {_eq: $codePanonceauCentrale}
            },
            _or: [
                {nom: {_ilike: $key}},
                {id_base_adherent: {_ilike: $key}},
                {libelle: {_ilike: $key}},
                {cp: {_ilike: $key}},
                {code_panonceau: {_ilike: $key}},
                {id_base_adherent_acdelec: {_ilike: $key}},
                {ville: {_ilike: $key}}
            ],
            est_concept: {_eq: false}
        }) {
            id_base_adherent
            code_activite
            code_panonceau
            id_base_adherent_acdelec
            cp
            nom
            libelle
            ville
            est_actif
            adresse
            zonechalandise {
              centrale {
                nom
              }
            }
        }
        centrale(where: {
            _or: [
                {code_panonceau_centrale: {_ilike: $keyCentral}},
                {nom: {_ilike: $keyCentral}}
            ]
        }) {
            nom
            code_panonceau_centrale
        }
    }
`;

const SelectPointVenteAutocompleteNational = gql`
query SelectPointVenteAutocomplete($key: String!, $keyCentral: String!) {
    pointvente(
        where: {
            est_actif: {_eq: true},
            zonechalandise: {code_panonceau_centrale: {}},
            _or: [
                {nom: {_ilike: $key}},
                {id_base_adherent: {_ilike: $key}},
                {libelle: {_ilike: $key}},
                {cp: {_ilike: $key}},
                {code_panonceau: {_ilike: $key}},
                {id_base_adherent_acdelec: {_ilike: $key}},
                {ville: {_ilike: $key}}
            ],
            est_concept: {_eq: false}
        }
    ) {
        id_base_adherent
        code_activite
        code_panonceau
        id_base_adherent_acdelec
        cp
        nom
        libelle
        ville
        est_actif
        adresse
        zonechalandise {
          centrale {
            nom
          }
        }
    }
    centrale(
        where: {
            _or: [
                {code_panonceau_centrale: {_ilike: $keyCentral}},
                {nom: {_ilike: $keyCentral}}
            ]
        }
    ) {
        nom
        code_panonceau_centrale
    }
}
`;

const SelectPointVenteAutocompleteWithInactive = gql`
    query SelectPointVenteAutocompleteWithInactive($codePanonceauCentrale: String, $key: String!, $keyCentral: String!) {
        pointvente(where: { zonechalandise: {code_panonceau_centrale: {_eq: $codePanonceauCentrale}},
            _or: [{nom: {_ilike: $key}}, {id_base_adherent: {_ilike: $key}}, {libelle: {_ilike: $key}},
                {cp: {_ilike: $key}}, {code_panonceau: {_ilike: $key}},{id_base_adherent_acdelec: {_ilike: $key}},
                {ville: {_ilike: $key}}]}) {
            id_base_adherent
            code_activite
            code_panonceau
            id_base_adherent_acdelec
            cp
            nom
            libelle
            ville
            est_actif
            adresse
            zonechalandise {
              centrale {
                nom
              }
            }
        }
        centrale(where: {_or: [{code_panonceau_centrale: {_ilike: $keyCentral}}, {nom: {_ilike: $keyCentral}}]}) {
            nom
            code_panonceau_centrale
        }
    }
`;

const SelectPointVenteAutocompleteWithInactiveNational = gql`
    query SelectPointVenteAutocompleteWithInactive($key: String!, $keyCentral: String!) {
  pointvente(where: {zonechalandise: {code_panonceau_centrale: {}}, _or: [{nom: {_ilike: $key}}, {id_base_adherent: {_ilike: $key}}, {libelle: {_ilike: $key}}, {cp: {_ilike: $key}}, {code_panonceau: {_ilike: $key}}, {id_base_adherent_acdelec: {_ilike: $key}}, {ville: {_ilike: $key}}]}) {
    id_base_adherent
    code_activite
    code_panonceau
    id_base_adherent_acdelec
    cp
    nom
    libelle
    ville
    est_actif
    adresse
    zonechalandise {
      centrale {
        nom
      }
    }
  }
  centrale(where: {_or: [{code_panonceau_centrale: {_ilike: $keyCentral}}, {nom: {_ilike: $keyCentral}}]}) {
    nom
    code_panonceau_centrale
  }
}
`;

const selectActiviteCommerciale =
    gql`
    query selectActiviteCommerciale {
        activitecommerciale (order_by: {libelle: asc}){
            code_activite
            libelle
        }
    }
`;
// id_base_adherent_acdelec
const UpdatePointVenteGql = gql`
    mutation UpdatePointVente($id_base_adherent: String!, $adresse: String!, $libelle: String, $ville: String!,
        $cp: String!, $gps_lat: String!, $gps_long: String!, $telephone: String!, )  {
        update_pointvente(where: {id_base_adherent: {_eq: $id_base_adherent}}, _set:
        {adresse: $adresse, libelle: $libelle, ville: $ville, cp: $cp, gps_lat: $gps_lat, gps_long: $gps_long,
            telephone: $telephone}) {
            affected_rows
        }
    }`;

const UpdatePointVenteVisuel = gql`
    mutation UpdatePointVente($id_base_adherent: String!, $visuel_desktop: String, $visuel_mobile: String)  {
        update_pointvente(where: {id_base_adherent: {_eq: $id_base_adherent}}, _set:
        {visuel_desktop: $visuel_desktop, visuel_mobile: $visuel_mobile}) {
            affected_rows
        }
    }`;
const UpdatePointVenteActiviteOrdre = gql`
    mutation UpdatePointVente($id_base_adherent: String!, $activite_ordre: Int!)  {
        update_pointvente(where: {id_base_adherent: {_eq: $id_base_adherent}}, _set:
        {activite_ordre: $activite_ordre}) {
            affected_rows
        }
    }`;

const UpdatePointVenteIdentifiant = gql`
    mutation UpdatePointVenteTriptychAndType(
        $old_id_base_adherent: String!,
        $new_id_base_adherent: String!,
        $code_panonceau: String!,
        $code_activite: String!,
        $numero_ordre: Int!,
        $est_master: Boolean!,
        $est_concept: Boolean!
    ) {
        update_pointvente(
            where: {id_base_adherent: {_eq: $old_id_base_adherent}},
            _set: {
                id_base_adherent: $new_id_base_adherent,
                code_panonceau: $code_panonceau,
                code_activite: $code_activite,
                numero_ordre: $numero_ordre,
                est_master: $est_master,
                est_concept: $est_concept
            }
        ) {
            affected_rows
        }
    }
`;

const DeletePointVenteMasterByConceptId = gql`
    mutation DeletePointVenteMasterByConceptId($id_pointvente_concept: uuid!) {
        delete_pointvente_master(where: {id_pointvente_concept: {_eq: $id_pointvente_concept}}) {
            affected_rows
        }
    }
`;

const numSirenInfo = gql`query selectDonneAdministratives($num_siren: String) {
    donnees_administratives(where: {numero_siren: {_eq: $num_siren}}) {
        adresse_siege_social
        capital_social
        code_postal_siege_social
        forme_sociale
        nom_adherent
        numero_rcs
        numero_siren
        numero_siret
        numero_tva_intra_commun
        email
        prenom_adherent
        raison_sociale
        ville_immatriculation
        ville_siege_social
        autorisations_etablissement
    }
}`;

const nifSpainInfo = gql`query selectDonneAdministratives($nif_espagnol: String) {
    donnees_administratives(where: {nif_espagnol: {_eq: $nif_espagnol}}) {
        adresse_siege_social
        capital_social
        code_postal_siege_social
        forme_sociale
        nom_adherent
        numero_rcs
        numero_siren
        nif_espagnol
        nif_portugais
        ms
        numero_fiscal
        paroisse_siege_social
        district_siege_social
        province_siege_social
        bureau_registre_commerce
        email
        cae
        numero_siret
        numero_tva_intra_commun
        prenom_adherent
        raison_sociale
        ville_immatriculation
        ville_siege_social
        autorisations_etablissement
    }
}`;

const nifPortugalInfo = gql`query selectDonneAdministratives($nif_portugal: Int) {
    donnees_administratives(where: {nif_portugais: {_eq: $nif_portugal}}) {
        adresse_siege_social
        capital_social
        code_postal_siege_social
        forme_sociale
        nom_adherent
        numero_rcs
        numero_siren
        nif_espagnol
        nif_portugais
        ms
        numero_fiscal
        paroisse_siege_social
        district_siege_social
        province_siege_social
        capitaux_propres
        bureau_registre_commerce
        email
        cae
        numero_siret
        numero_tva_intra_commun
        prenom_adherent
        raison_sociale
        ville_immatriculation
        ville_siege_social
        autorisations_etablissement
    }
}`;
const msInfo = gql`query selectDonneAdministratives($ms: Int) {
    donnees_administratives(where: {ms: {_eq: $ms}}) {
        adresse_siege_social
        capital_social
        code_postal_siege_social
        forme_sociale
        nom_adherent
        numero_rcs
        numero_siren
        nif_espagnol
        nif_portugais
        ms
        numero_fiscal
        paroisse_siege_social
        district_siege_social
        province_siege_social
        capitaux_propres
        bureau_registre_commerce
        email
        cae
        numero_siret
        numero_tva_intra_commun
        prenom_adherent
        raison_sociale
        ville_immatriculation
        ville_siege_social
        autorisations_etablissement
    }
}`;
/**
 * Point vente horaire query repository.
 */
const LoadAllPointVente = gql`
    query getAll(
        $codePanonceau: String!,
        $date: date
    ) {
        pointvente(
            where: {
                code_panonceau: {_eq: $codePanonceau},
                est_actif: {_eq: true}
            },
            order_by: {
                est_master: desc,
                libelle: asc
            }
        ) {
            ${ALL_POINTVENTE_FIELD}
            horaires {
                ${ALL_HORAIRE_FIELD}
            }
            ouvertures_fermetures_exceptionnelles(
                where: {
                    _or: [
                        {date_fin: {_gte: $date}},
                        {date: {_gte: $date}}
                    ]
                },
                order_by: {date: asc}
            ) {
                ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
            }
        }
    }
`;

const LoadAllPointVenteWithId = gql`
    query getAll(
        $id_pointvente: uuid!,
        $date: date
    ) {
        pointvente(
            where: {
                _or: [
                    {id_pointvente: {_eq: $id_pointvente}},
                    {pointventes_master: {id_pointvente_master: {_eq: $id_pointvente}}}
                ],
                est_actif: {_eq: true}
            },
            order_by: {
                est_master: desc,
                libelle: asc
            }
        ) {
            ${ALL_POINTVENTE_FIELD}
            horaires {
                ${ALL_HORAIRE_FIELD}
            }
            ouvertures_fermetures_exceptionnelles(
                where: {
                    _or: [
                        {date_fin: {_gte: $date}},
                        {date: {_gte: $date}}
                    ]
                },
                order_by: {date: asc}
            ) {
                ${ALL_HORAIRE_EXCEPTIONNELLE_FIELD}
            }
        }
    }
`;

// url_magasin  ville  gps_lat
const insertPointDeVenteConcept = gql`
    mutation insertPointDevente(
        $adresse: String,
        $code_activite: String,
        $code_panonceau: String,
        $id_base_adherent: String,
        $id_base_adherent_acdelec: String,
        $cp: String,
        $ville: String,
        $telephone: String,
        $url_magasin: String,
        $est_actif: Boolean,
        $gps_long: String,
        $gps_lat: String,
        $est_master: Boolean,
        $est_concept: Boolean,
        $numero_siren: String,
        $nom: String,
        $libelle: String,
        $id_gouv: String,
        $province: String,
        $district: String,
        $numero_ordre: Int,
        $donnee_administrative: donnees_administratives_insert_input!,
        $infos_reglementaires_parite: [informations_reglementaires_parite_insert_input!]!,
        $pointvente_masters: [pointvente_master_insert_input!]!,
        $horaires: [horaire_insert_input!]!
    ) {
        insert_pointvente(objects: {
            adresse: $adresse,
            code_activite: $code_activite,
            code_panonceau: $code_panonceau,
            id_base_adherent_acdelec: $id_base_adherent_acdelec,
            id_base_adherent: $id_base_adherent,
            cp: $cp,
            ville: $ville,
            telephone: $telephone,
            url_magasin: $url_magasin,
            est_actif: $est_actif,
            est_master: $est_master,
            est_concept: $est_concept,
            gps_lat: $gps_lat,
            gps_long: $gps_long,
            id_gouv: $id_gouv,
            libelle: $libelle,
            nom: $nom,
            numero_ordre: $numero_ordre,
            province: $province,
            district: $district,
            donnee_administrative: {
                on_conflict: {
                    constraint: donnee_administratives_pkey,
                    update_columns: [
                        numero_siret,
                        adresse_siege_social,
                        capital_social,
                        code_postal_siege_social,
                        forme_sociale,
                        nom_adherent,
                        numero_rcs,
                        numero_siret,
                        numero_tva_intra_commun,
                        prenom_adherent,
                        raison_sociale,
                        ville_immatriculation,
                        ville_siege_social,
                        nif_espagnol,
                        nif_portugais,
                        ms,
                        numero_fiscal,
                        paroisse_siege_social,
                        district_siege_social,
                        province_siege_social,
                        capitaux_propres,
                        bureau_registre_commerce,
                        email,
                        cae
                    ]
                },
                data: $donnee_administrative
            },
            horaires: {data: $horaires},
            informations_reglementaires_parites: {
                on_conflict: {
                    constraint: informations_reglementaires_parite_pkey,
                    update_columns: [
                        ecart_remuneration,
                        parite_haute_remuneration,
                        pourcentage_salarie_augmentation_individuelles,
                        ecart_taux_augmentation_individuelles,
                        ecart_taux_promotion,
                        updated_at
                    ]
                },
                data: $infos_reglementaires_parite
            }

            pointventes_master: {
                data: $pointvente_masters,
                on_conflict: {
                    constraint: pointvente_master_pkey,
                    update_columns: [id_pointvente_master]
                }
            }
        }
    ) {
        affected_rows
    }
}
`;

const insertPointDeVenteMaster = gql`
    mutation insertPointDevente(
        $adresse: String,
        $code_activite: String,
        $code_panonceau: String,
        $id_base_adherent: String,
        $id_base_adherent_acdelec: String,
        $cp: String,
        $ville: String,
        $telephone: String,
        $url_magasin: String,
        $est_actif: Boolean,
        $gps_long: String,
        $gps_lat: String,
        $est_master: Boolean,
        $est_concept: Boolean,
        $numero_siren: String,
        $nom: String,
        $libelle: String,
        $id_gouv: String,
        $province: String,
        $district: String,
        $numero_ordre: Int,
        $donnee_administrative: donnees_administratives_insert_input!,
        $infos_reglementaires_parite: [informations_reglementaires_parite_insert_input!]!,
        $horaires: [horaire_insert_input!]!
    ) {
        insert_pointvente(objects: {
            adresse: $adresse,
            code_activite: $code_activite,
            code_panonceau: $code_panonceau,
            id_base_adherent_acdelec: $id_base_adherent_acdelec,
            id_base_adherent: $id_base_adherent,
            cp: $cp,
            ville: $ville,
            telephone: $telephone,
            url_magasin: $url_magasin,
            est_actif: $est_actif,
            est_master: $est_master,
            est_concept: $est_concept,
            gps_lat: $gps_lat,
            gps_long: $gps_long,
            id_gouv: $id_gouv,
            libelle: $libelle,
            nom: $nom,
            numero_ordre: $numero_ordre,
            province: $province,
            district: $district,
            donnee_administrative: {
                on_conflict: {
                    constraint: donnee_administratives_pkey,
                    update_columns: [
                        numero_siret,
                        adresse_siege_social,
                        capital_social,
                        code_postal_siege_social,
                        forme_sociale,
                        nom_adherent,
                        numero_rcs,
                        numero_siret,
                        numero_tva_intra_commun,
                        prenom_adherent,
                        raison_sociale,
                        ville_immatriculation,
                        ville_siege_social,
                        nif_espagnol,
                        nif_portugais,
                        ms,
                        numero_fiscal,
                        paroisse_siege_social,
                        district_siege_social,
                        province_siege_social,
                        capitaux_propres,
                        bureau_registre_commerce,
                        email,
                        cae
                    ]
                },
                data: $donnee_administrative
            },
            horaires: {data: $horaires},
            informations_reglementaires_parites: {
                on_conflict: {
                    constraint: informations_reglementaires_parite_pkey,
                    update_columns: [
                        ecart_remuneration,
                        parite_haute_remuneration,
                        pourcentage_salarie_augmentation_individuelles,
                        ecart_taux_augmentation_individuelles,
                        ecart_taux_promotion,
                        updated_at
                    ]
                },
                data: $infos_reglementaires_parite
            }
        }
    ) {
            affected_rows
    }
}
`;

const updatePointDeventeMasterDonneAdministrativeHoraires = gql`
    mutation updatePointDevente(
        $id_pointvente: uuid,
        $adresse: String,
        $code_activite: String,
        $code_panonceau: String,
        $id_base_adherent_acdelec: String,
        $cp: String,
        $ville: String,
        $telephone: String,
        $url_magasin: String,
        $est_actif: Boolean,
        $gps_long: String,
        $gps_lat: String,
        $est_master: Boolean,
        $est_concept: Boolean,
        $numero_siren: String,
        $nom: String,
        $libelle: String,
        $id_gouv: String,
        $id_base_adherent: String,
        $province: String,
        $district: String,
        $numero_ordre: Int,
        $donnee_administrative: donnees_administratives_insert_input!,
        $horaires: [horaire_insert_input!]!
    ) {
        insert_pointvente(objects: {
            id_pointvente: $id_pointvente,
            adresse: $adresse,
            code_activite: $code_activite,
            code_panonceau: $code_panonceau,
            id_base_adherent_acdelec: $id_base_adherent_acdelec,
            cp: $cp,
            ville: $ville,
            telephone: $telephone,
            url_magasin: $url_magasin,
            est_actif: $est_actif,
            est_master: $est_master,
            est_concept: $est_concept,
            gps_lat: $gps_lat,
            gps_long: $gps_long,
            id_base_adherent: $id_base_adherent,
            id_gouv: $id_gouv,
            libelle: $libelle,
            nom: $nom,
            numero_ordre: $numero_ordre,
            province: $province,
            district: $district,
            donnee_administrative: {
                on_conflict: {
                    constraint: donnee_administratives_pkey,
                    update_columns: [
                        numero_siren,
                        numero_siret,
                        adresse_siege_social,
                        capital_social,
                        code_postal_siege_social
                        forme_sociale,
                        nom_adherent,
                        numero_rcs,
                        numero_siret,
                        numero_tva_intra_commun,
                        prenom_adherent,
                        raison_sociale,
                        ville_immatriculation,
                        ville_siege_social,
                        nif_espagnol,
                        nif_portugais,
                        ms,
                        numero_fiscal,
                        paroisse_siege_social,
                        district_siege_social,
                        province_siege_social,
                        capitaux_propres,
                        bureau_registre_commerce,
                        email,
                        cae
                    ]
                },
                data: $donnee_administrative
            },
            horaires: {
                data: $horaires,
                on_conflict: {
                    constraint: horaire_pkey,
                    update_columns: [
                        horaire_fermeture_apm,
                        horaire_fermeture_matin,
                        horaire_ouverture_apm,
                        horaire_ouverture_matin,
                        nom_jour
                    ]
                }
            }
        },
        on_conflict: {
            constraint: pointvente_pkey,
            update_columns: [
                adresse,
                code_activite,
                code_panonceau,
                id_base_adherent_acdelec,
                cp,
                ville,
                telephone,
                url_magasin,
                est_actif,
                est_master,
                est_concept,
                gps_lat,
                gps_long,
                id_base_adherent,
                id_gouv,
                libelle,
                nom,
                numero_ordre,
                numero_siren,
                district,
                province
            ]
        }) {
            affected_rows
        }
    }
`;

const updatePointDeventeConceptDonneAdministrativeHoraires = gql`
    mutation updatePointDevente(
        $id_pointvente: uuid,
        $adresse: String,
        $code_activite: String,
        $code_panonceau: String,
        $id_base_adherent_acdelec: String,
        $cp: String,
        $ville: String,
        $telephone: String,
        $url_magasin: String,
        $est_actif: Boolean,
        $gps_long: String,
        $gps_lat: String,
        $est_master: Boolean,
        $est_concept: Boolean,
        $numero_siren: String,
        $nom: String,
        $libelle: String,
        $id_gouv: String,
        $id_base_adherent: String,
        $province: String,
        $district: String,
        $numero_ordre: Int,
        $donnee_administrative: donnees_administratives_insert_input!,
        $horaires: [horaire_insert_input!]!
        $pointvente_masters: [pointvente_master_insert_input!]!,
    ) {
        delete_pointvente_master(where: {id_pointvente_concept: {_eq: $id_pointvente}}) {
            affected_rows
        }
        insert_pointvente(objects: {
            id_pointvente: $id_pointvente,
            adresse: $adresse,
            code_activite: $code_activite,
            code_panonceau: $code_panonceau,
            id_base_adherent_acdelec: $id_base_adherent_acdelec,
            cp: $cp,
            ville: $ville,
            telephone: $telephone,
            url_magasin: $url_magasin,
            est_actif: $est_actif,
            est_master: $est_master,
            est_concept: $est_concept,
            gps_lat: $gps_lat,
            gps_long: $gps_long,
            id_base_adherent: $id_base_adherent,
            id_gouv: $id_gouv,
            libelle: $libelle,
            nom: $nom,
            numero_ordre: $numero_ordre,
            province: $province,
            district: $district,
            donnee_administrative: {
                on_conflict: {
                    constraint: donnee_administratives_pkey,
                    update_columns: [
                        numero_siren,
                        numero_siret,
                        adresse_siege_social,
                        capital_social,
                        code_postal_siege_social
                        forme_sociale,
                        nom_adherent,
                        numero_rcs,
                        numero_siret,
                        numero_tva_intra_commun,
                        prenom_adherent,
                        raison_sociale,
                        ville_immatriculation,
                        ville_siege_social,
                        nif_espagnol,
                        nif_portugais,
                        ms,
                        numero_fiscal,
                        paroisse_siege_social,
                        district_siege_social,
                        province_siege_social,
                        capitaux_propres,
                        bureau_registre_commerce,
                        email,
                        cae
                    ]
                },
                data: $donnee_administrative
            },
            

            pointventes_master: {
                data: $pointvente_masters,
                on_conflict: {
                    constraint: pointvente_master_pkey,
                    update_columns: [id_pointvente_master]
                }
            },

            horaires: {
                data: $horaires,
                on_conflict: {
                    constraint: horaire_pkey,
                    update_columns: [
                        horaire_fermeture_apm,
                        horaire_fermeture_matin,
                        horaire_ouverture_apm,
                        horaire_ouverture_matin,
                        nom_jour
                    ]
                }
            }
        },
        on_conflict: {
            constraint: pointvente_pkey,
            update_columns: [
                adresse,
                code_activite,
                code_panonceau,
                id_base_adherent_acdelec,
                cp,
                ville,
                telephone,
                url_magasin,
                est_actif,
                est_master,
                est_concept,
                gps_lat,
                gps_long,
                id_base_adherent,
                id_gouv,
                libelle,
                nom,
                numero_ordre,
                numero_siren,
                district,
                province
            ]
        }) {
            affected_rows
        }
    }
`;

// id_base_adherent_acdelec
// update pointDeVente
const updatePointDeventeDonneAdministrativeHoraires = gql`
    mutation updatePointDevente(
        $id_pointvente: uuid,
        $adresse: String,
        $code_activite: String,
        $code_panonceau: String,
        $id_base_adherent_acdelec: String,
        $cp: String,
        $ville: String,
        $telephone: String,
        $url_magasin: String,
        $est_actif: Boolean,
        $gps_long: String,
        $gps_lat: String,
        $est_master: Boolean,
        $est_concept: Boolean,
        $numero_siren: String,
        $nom: String,
        $libelle: String,
        $id_gouv: String,
        $id_base_adherent: String,
        $province: String,
        $district: String,
        $numero_ordre: Int,
        $donnee_administrative: donnees_administratives_insert_input!,
        $horaires: [horaire_insert_input!]!
    ) {
        insert_pointvente(objects: {
            adresse: $adresse,
            code_activite: $code_activite,
            code_panonceau: $code_panonceau,
            id_base_adherent_acdelec: $id_base_adherent_acdelec,
            cp: $cp,
            ville: $ville,
            telephone: $telephone,
            url_magasin: $url_magasin,
            est_actif: $est_actif,
            est_master: $est_master,
            est_concept: $est_concept,
            gps_lat: $gps_lat,
            gps_long: $gps_long,
            id_base_adherent: $id_base_adherent,
            id_gouv: $id_gouv,
            libelle: $libelle,
            nom: $nom,
            numero_ordre: $numero_ordre,
            province: $province,
            district: $district,
            donnee_administrative: {
                on_conflict: {
                    constraint: donnee_administratives_pkey,
                    update_columns: [
                        numero_siren,
                        numero_siret,
                        adresse_siege_social,
                        capital_social,
                        code_postal_siege_social
                        forme_sociale,
                        nom_adherent,
                        numero_rcs,
                        numero_siret,
                        numero_tva_intra_commun,
                        prenom_adherent,
                        raison_sociale,
                        ville_immatriculation,
                        ville_siege_social,
                        nif_espagnol,
                        nif_portugais,
                        ms,
                        numero_fiscal,
                        paroisse_siege_social,
                        district_siege_social,
                        province_siege_social,
                        capitaux_propres,
                        bureau_registre_commerce,
                        email,
                        cae
                    ]
                },
                data: $donnee_administrative
            },
            horaires: {
                data: $horaires,
                on_conflict: {
                    constraint: horaire_pkey,
                    update_columns: [
                        horaire_fermeture_apm,
                        horaire_fermeture_matin,
                        horaire_ouverture_apm,
                        horaire_ouverture_matin,
                        nom_jour
                    ]
                }
            }
        },
        on_conflict: {
            constraint: pointvente_pkey,
            update_columns: [
                adresse,
                code_activite,
                code_panonceau,
                id_base_adherent_acdelec,
                cp,
                ville,
                telephone,
                url_magasin,
                est_actif,
                est_master,
                est_concept,
                gps_lat,
                gps_long,
                id_base_adherent,
                id_gouv,
                libelle,
                nom,
                numero_ordre,
                numero_siren,
                district,
                province
            ]
        }) {
            affected_rows
        }
    }
`;

const syncPointVenteMasters = gql`
    mutation syncPointVenteMasters(
        $id_pointvente_concept: uuid!,
        $pointvente_master_objects: [pointvente_master_insert_input!]!
    ) {
        delete_pointvente_master(where: {id_pointvente_concept: {_eq: $id_pointvente_concept}}) {
            affected_rows
        }
        insert_pointvente_master(
            objects: $pointvente_master_objects,
            on_conflict: {constraint: pointvente_master_pkey, update_columns: [id_pointvente_master]}
        ) {
            affected_rows
        }
    }
`;

const selectPointDeVenteByiD = gql`
    query selectPointDeVenteByiD($id_base_adherent: String ) {
        pointvente(where: {id_base_adherent: {_eq: $id_base_adherent}}) {
            ${ALL_POINTVENTE_FIELD}

            pointventes_master {
                ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
                master_pointvente {
                    ${ALL_POINTVENTE_FIELD}
                }
            }
        
            pointventes_concept {
                ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
                concept_pointvente {
                    ${ALL_POINTVENTE_FIELD}
                }
            }
            horaires {
                code_jour
                horaire_fermeture_apm
                horaire_fermeture_matin
                horaire_ouverture_apm
                horaire_ouverture_matin
                nom_jour
            }
            donnee_administrative {
                adresse_siege_social
                capital_social
                code_postal_siege_social
                forme_sociale
                nom_adherent
                numero_rcs
                numero_siren
                numero_siret
                numero_tva_intra_commun
                prenom_adherent
                raison_sociale
                ville_immatriculation
                ville_siege_social
                autorisations_etablissement
                nif_espagnol
                nif_portugais
                paroisse_siege_social
                district_siege_social
                province_siege_social
                capitaux_propres
                bureau_registre_commerce
                cae
                email
                ms
                numero_fiscal
            }
            informations_reglementaires_parites {
                ${ALL_INFORMATIONS_REGLEMENTAIRES_PARITE_FIELD}
            }
        }
    }
`;

const selectPointDeVenteByIdsBadh = gql`
    query selectPointDeVenteByIdsBadh($idsBadh: [String!] ) {
        pointvente(where: {id_base_adherent: {_in: $idsBadh}}) {
            ${ALL_POINTVENTE_FIELD}

            pointventes_master {
                ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
                master_pointvente {
                    ${ALL_POINTVENTE_FIELD}
                }
            }
        
            pointventes_concept {
                ${ALL_POINTVENTE_MASTER_CONCEPT_FIELD}
                concept_pointvente {
                    ${ALL_POINTVENTE_FIELD}
                }
            }
            horaires {
                code_jour
                horaire_fermeture_apm
                horaire_fermeture_matin
                horaire_ouverture_apm
                horaire_ouverture_matin
                nom_jour
            }
            donnee_administrative {
                adresse_siege_social
                capital_social
                code_postal_siege_social
                forme_sociale
                nom_adherent
                numero_rcs
                numero_siren
                numero_siret
                numero_tva_intra_commun
                prenom_adherent
                raison_sociale
                ville_immatriculation
                ville_siege_social
                autorisations_etablissement
                nif_espagnol
                nif_portugais
                paroisse_siege_social
                district_siege_social
                province_siege_social
                capitaux_propres
                bureau_registre_commerce
                cae
                email
                ms
                numero_fiscal
            }
            informations_reglementaires_parites {
                ${ALL_INFORMATIONS_REGLEMENTAIRES_PARITE_FIELD}
            }
        }
    }
`;

const selectNumeroOrdre = gql`query selectNumeroOrdre($id_base_adherent: String! ) {
    pointvente(where: {id_base_adherent: {_like: $id_base_adherent}}) {
      numero_ordre
    }
  }`;

const updateDonnesAdministratives = gql`
    mutation updateDonnesAdministratives(
        $numero_siren: String,
        $adresse_siege_social: String,
        $code_postal_siege_social: String,
        $forme_sociale: String,
        $nom_adherent: String = "",
        $numero_rcs: String,
        $numero_siren: String,
        $numero_siret: String,
        $numero_tva_intra_commun: String,
        $prenom_adherent: String,
        $raison_sociale: String,
        $ville_immatriculation: String,
        $ville_siege_social: String,
        $capital_social: String,
        $province_siege_social: String,
        $bureau_registre_commerce: String,
        $numero_fiscal: String,
        $ms: Int,
        $cae: Int,
        $capitaux_propres: String,
        $district_siege_social: String,
        $paroisse_siege_social: String,
        $nif_portugais: String,
        $nif_espagnol: String,
        $email: String
    ) {
        update_donnees_administratives(
            where: {numero_siren: {_eq: $numero_siren}},
            _set: {
                adresse_siege_social: $adresse_siege_social,
                capital_social: $capital_social,
                code_postal_siege_social: $code_postal_siege_social,
                forme_sociale: $forme_sociale,
                nom_adherent: $nom_adherent,
                numero_rcs: $numero_rcs,
                numero_siren: $numero_siren,
                numero_siret: $numero_siret,
                numero_tva_intra_commun: $numero_tva_intra_commun,
                prenom_adherent: $prenom_adherent,
                raison_sociale: $raison_sociale,
                ville_immatriculation: $ville_immatriculation,
                ville_siege_social: $ville_siege_social,
                nif_espagnol: $nif_espagnol,
                nif_portugais: $nif_portugais,
                paroisse_siege_social: $paroisse_siege_social,
                district_siege_social: $district_siege_social,
                province_siege_social: $province_siege_social,
                capitaux_propres: $capitaux_propres,
                bureau_registre_commerce: $bureau_registre_commerce,
                cae: $cae,
                email: $email,
                ms: $ms,
                numero_fiscal: $numero_fiscal
            }
        ) {
            affected_rows
        }
    }
`;

const GET_ALL_POINT_VENTE_NATIONAL_IS_ACTIF = gql`query ($isActive: Boolean) {
    pointvente(where: {est_actif: {_eq: $isActive}}) {
        ${ALL_POINTVENTE_FIELD}
        horaires {
            ${ALL_HORAIRE_FIELD}
        }
    }
}`;

const GET_ALL_POINT_VENTE_CENTRALE_IS_ACTIF = gql`query ($codePanonceauCentrale: String, $isActive: Boolean) {
    pointvente(where: {zonechalandise: {centrale: {code_panonceau_centrale: {_eq: $codePanonceauCentrale}}}, est_actif: {_eq: $isActive}}) {
        ${ALL_POINTVENTE_FIELD}
        horaires {
            ${ALL_HORAIRE_FIELD}
        }
    }
}`;


